import * as React from 'react'
import { useCallback, useState } from 'react'

import { WorkflowIcon } from 'lucide-react'
import { inject, observer } from 'mobx-react'

import { cn } from '@/lib/utils'

import { linkedWorkflowLikeFieldTypes } from '../../constants/PermitFieldTypeConstants'
import Localization from '../../localization/LocalizationManager'
import Workflow from '../../models/Workflow'
import PermitInspectionChangesStore from '../../stores/domain/PermitInspectionChanges.store'
import PermitInspectionsStore from '../../stores/domain/PermitInspections.store'
import { Button } from '../Button/Button'
import { CommonDateFilterMode } from '../CommonDateFilter'
import { ListSelector } from '../ListSelector'
import ResponsiveSurface from '../ResponsiveSurface/ResponsiveSurface'
import WorkflowDirectory from '../WorkflowDirectory/WorkflowDirectory'

interface IProps {
  workflow: Workflow
  permitInspectionsStore?: PermitInspectionsStore
  permitInspectionChangesStore?: PermitInspectionChangesStore
  isMobileEnv?: boolean
  className?: string

  onWorkflowClick?: (workflow: Workflow) => void
  displayOnly?: boolean
}

const WorkflowBacklinksComponent: React.FC<IProps> = ({
  workflow,
  permitInspectionsStore,
  permitInspectionChangesStore,
  isMobileEnv,
  className,
  onWorkflowClick,
  displayOnly,
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const inspections =
    permitInspectionsStore?.getInspectionsForPermit(workflow.id) ?? []
  const inspectionLinkedIds = inspections.flatMap(i => {
    const latestChange =
      permitInspectionChangesStore?.getLatestChangeForInspection(i.id)
    const effective = latestChange?.toInspection(i.permitId) || i
    return linkedWorkflowLikeFieldTypes
      .flatMap(t => effective.getAllFieldsByType(t))
      .flatMap(f => (f.values as string[]) ?? [])
  })
  const linkedWorkflowIds = [
    ...new Set([...workflow.allLinkedWorkflowIds, ...inspectionLinkedIds]),
  ]

  const handleWorkflowClick = useCallback(
    (clickedWorkflow: Workflow) => {
      onWorkflowClick?.(clickedWorkflow)
      setIsOpen(false)
    },
    [onWorkflowClick],
  )

  const stopPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
  }, [])

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  if (!linkedWorkflowIds.length) {
    return null
  }

  const tooltipContent = `${linkedWorkflowIds.length} ${Localization.translator.linkedWorkflows}`

  const triggerContent = (
    <>
      {linkedWorkflowIds.length}
      <WorkflowIcon data-icon="inline-end" className="tw:size-3.5" />
    </>
  )

  if (displayOnly) {
    return (
      <span
        title={tooltipContent}
        className={cn(
          'tw:ml-1 tw:inline-flex tw:items-center tw:gap-1 tw:text-app-dense-medium tw:text-primary',
          className,
        )}
      >
        {triggerContent}
      </span>
    )
  }

  return (
    <ResponsiveSurface
      open={isOpen}
      onOpenChange={setIsOpen}
      title={Localization.translator.linkedWorkflows}
      isMobileMode={isMobileEnv}
      sizing="fill"
      width="list"
      backdrop
      side="top"
      align="end"
      trigger={
        <Button
          variant="primary-ghost"
          size="xs"
          className={cn('tw:ml-1', className)}
          aria-label={tooltipContent}
          title={tooltipContent}
          onClick={stopPropagation}
        >
          {triggerContent}
        </Button>
      }
    >
      <ListSelector.Title
        title={Localization.translator.linkedWorkflows}
        onClose={handleClose}
      />
      <WorkflowDirectory
        isReadOnly
        restrictToIds={linkedWorkflowIds}
        onWorkflowClick={handleWorkflowClick}
        onCancel={handleClose}
        allowCreateNew={false}
        showQuickFilters={true}
        displayOnlyBacklinks
        initialDateFilterMode={CommonDateFilterMode.PROJECT}
      />
    </ResponsiveSurface>
  )
}

const WorkflowBacklinks = inject(
  'permitInspectionsStore',
  'permitInspectionChangesStore',
  'isMobileEnv',
)(observer(WorkflowBacklinksComponent))

export default WorkflowBacklinks
